// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import useRequestRegistrarService from '../../../hooks/mutations/registry/onboarding/useRequestRegistrarService';
import useMutate from '../../../hooks/mutations/useMutate';
import useRegistrarServiceRequests from '../../../hooks/queries/registry/onboarding/useRegistrarServiceRequests';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import { ERR_REQ_REGISTRAR_SVC, SUC_REQ_REGISTRAR_SVC } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestRegistrarServiceDialog = ({ open, onClose }: DialogProps) => {
  const [provider, setProvider] = useState<string>('');
  const requestRegistrarService = useRequestRegistrarService();
  const mutate = useMutate();

  // Check if service is already requested or provided
  const { party } = useParties();
  const registrarServices = useRegistrarServices();
  const registrarServiceRequests = useRegistrarServiceRequests();
  const hasService = !!registrarServices.data?.find(
    (r) => r.payload.provider === provider && r.payload.registrar === party,
  );
  const hasPendingRequest = !!registrarServiceRequests.data?.find(
    (r) => r.payload.provider === provider && r.payload.registrar === party,
  );

  const resetState = useCallback(() => {
    setProvider('');
  }, [setProvider]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const payload = {
      provider,
    };
    const res = await mutate(
      requestRegistrarService,
      payload,
      SUC_REQ_REGISTRAR_SVC,
      ERR_REQ_REGISTRAR_SVC,
    );
    handleClose();
    return res;
  }, [requestRegistrarService, provider, handleClose, mutate]);

  const isLoading = false;
  if (isLoading) return null;

  const disableSubmit = !provider || hasService || hasPendingRequest;

  return (
    <FormDialog
      open={open}
      title="Request Registrar Service"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput label="Provider" value={provider} setValue={setProvider} required />
    </FormDialog>
  );
};
